import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { app } from '../../services/firebase';
import { checkUsernameAvailability, validateUsernameFormat, reserveUsername, releaseUsername } from '../../services/usernameService';
import { CountryLanguageSelector } from '../auth/CountryLanguageSelector';
import './UserMenu.css';

const db = getFirestore(app);

// Social media validation functions
function validateTwitterHandle(handle: string): boolean {
  if (!handle) return true;
  const hasInvalidDomain = /https?:\/\/(www\.)?(github\.com|youtube\.com|youtu\.be|facebook\.com|instagram\.com|linkedin\.com)/i.test(handle);
  if (hasInvalidDomain) return false;
  if (/^https?:\/\/(www\.)?(twitter\.com|x\.com)\//i.test(handle)) {
    return /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9_]{1,15}$/i.test(handle);
  }
  const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle;
  return /^[A-Za-z0-9_]{1,15}$/.test(cleanHandle);
}

function validateGitHubUsername(username: string): boolean {
  if (!username) return true;
  const hasInvalidDomain = /https?:\/\/(www\.)?(twitter\.com|x\.com|youtube\.com|youtu\.be|facebook\.com|instagram\.com|linkedin\.com)/i.test(username);
  if (hasInvalidDomain) return false;
  if (/^https?:\/\/(www\.)?github\.com\//i.test(username)) {
    return /^https?:\/\/(www\.)?github\.com\/[A-Za-z0-9-]{1,39}$/i.test(username);
  }
  return /^[A-Za-z0-9-]{1,39}$/.test(username) && !username.startsWith('-') && !username.endsWith('-');
}

function validateYouTubeUrl(url: string): boolean {
  if (!url) return true;
  const hasInvalidDomain = /https?:\/\/(www\.)?(twitter\.com|x\.com|github\.com|facebook\.com|instagram\.com|linkedin\.com)/i.test(url);
  if (hasInvalidDomain) return false;
  const youtubePatterns = [
    /^https?:\/\/(www\.)?youtube\.com\/(channel\/|c\/|user\/|@)?[A-Za-z0-9_-]+/i,
    /^https?:\/\/youtu\.be\/[A-Za-z0-9_-]+/i,
    /^@[A-Za-z0-9_-]+$/
  ];
  return youtubePatterns.some(pattern => pattern.test(url));
}

interface UserMenuProps {
  username: string;
  onClose: () => void;
}

export default function UserMenu({ username, onClose }: UserMenuProps) {
  const { currentUser } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [equipmentExpanded, setEquipmentExpanded] = useState(false);
  const [socialExpanded, setSocialExpanded] = useState(false);
  
  const [settings, setSettings] = useState({
    username: username,
    nationality: 'US',
    language: 'en',
    consentToShareImprovementScore: false,
    consentToDisplaySetupData: false,
    consentToDisplaySocialLinks: false,
    setupDetails: {
      keyboardMake: '',
      keyboardModel: '',
      switches: '',
      os: '',
      refreshRate: 60
    },
    socialLinks: {
      twitter: '',
      github: '',
      youtube: ''
    }
  });

  const [originalSettings, setOriginalSettings] = useState(settings);
  const [usernameStatus, setUsernameStatus] = useState<'checking' | 'available' | 'taken' | 'invalid' | 'unchanged'>('unchanged');
  const [originalUsername, setOriginalUsername] = useState(username);

  // Load user settings
  useEffect(() => {
    if (currentUser) {
      const loadSettings = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const loadedSettings = {
              username: data.username || username,
              nationality: data.nationality || 'US',
              language: data.language || 'en',
              consentToShareImprovementScore: data.consentToShareImprovementScore || false,
              consentToDisplaySetupData: data.consentToDisplaySetupData || false,
              consentToDisplaySocialLinks: data.consentToDisplaySocialLinks || false,
              setupDetails: data.setupDetails || {},
              socialLinks: data.socialLinks || {}
            };
            setSettings(loadedSettings);
            setOriginalSettings(loadedSettings);
            setOriginalUsername(loadedSettings.username);
          }
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      };
      loadSettings();
    }
  }, [currentUser, username]);

  // Check for unsaved changes
  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasUnsavedChanges(changed);
  }, [settings, originalSettings]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Username validation
  useEffect(() => {
    if (settings.username === originalUsername) {
      setUsernameStatus('unchanged');
      return;
    }

    const formatCheck = validateUsernameFormat(settings.username);
    if (!formatCheck.valid) {
      setUsernameStatus('invalid');
      return;
    }

    const checkAvailability = async () => {
      setUsernameStatus('checking');
      const isAvailable = await checkUsernameAvailability(settings.username);
      setUsernameStatus(isAvailable ? 'available' : 'taken');
    };

    const debounce = setTimeout(checkAvailability, 500);
    return () => clearTimeout(debounce);
  }, [settings.username, originalUsername]);

  const handleSave = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Handle username change
      if (settings.username !== originalUsername) {
        if (usernameStatus !== 'available') {
          alert('Please choose a valid and available username');
          setSaving(false);
          return;
        }
        await reserveUsername(settings.username, currentUser.uid);
        if (originalUsername) {
          await releaseUsername(originalUsername);
        }
      }

      // Save all settings
      await setDoc(doc(db, 'users', currentUser.uid), settings, { merge: true });
      
      setOriginalSettings(settings);
      setOriginalUsername(settings.username);
      setHasUnsavedChanges(false);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="user-menu-overlay">
      <div className="user-menu" ref={menuRef}>
        <div className="user-menu-header">
          <h3>User Settings</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {hasUnsavedChanges && (
          <div className="unsaved-indicator">
            ⚠️ You have unsaved changes
          </div>
        )}

        <div className="user-menu-content">
          {/* Username */}
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={settings.username}
              onChange={(e) => setSettings({...settings, username: e.target.value})}
            />
            {usernameStatus === 'checking' && <span className="status checking">Checking...</span>}
            {usernameStatus === 'available' && <span className="status available">✓ Available</span>}
            {usernameStatus === 'taken' && <span className="status taken">✗ Taken</span>}
            {usernameStatus === 'invalid' && <span className="status invalid">✗ Invalid format</span>}
          </div>

          {/* Nationality & Language */}
          <CountryLanguageSelector
            nationality={settings.nationality}
            language={settings.language}
            onNationalityChange={(nat: string) => setSettings({...settings, nationality: nat})}
            onLanguageChange={(lang: string) => setSettings({...settings, language: lang})}
          />

          {/* Privacy Settings */}
          <div className="form-section">
            <h4>Privacy Preferences</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.consentToShareImprovementScore}
                onChange={(e) => setSettings({...settings, consentToShareImprovementScore: e.target.checked})}
              />
              Share improvement score on leaderboard
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.consentToDisplaySetupData}
                onChange={(e) => setSettings({...settings, consentToDisplaySetupData: e.target.checked})}
              />
              Display equipment setup
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.consentToDisplaySocialLinks}
                onChange={(e) => setSettings({...settings, consentToDisplaySocialLinks: e.target.checked})}
              />
              Display social links
            </label>
          </div>

          {/* Equipment Setup - Collapsible */}
          <div className="form-section collapsible">
            <button 
              className="section-toggle"
              onClick={() => setEquipmentExpanded(!equipmentExpanded)}
            >
              <span>Equipment Setup</span>
              <span className="toggle-icon">{equipmentExpanded ? '▼' : '▶'}</span>
            </button>
            {equipmentExpanded && (
              <div className="section-content">
                <div className="form-group">
                  <label>Keyboard Make</label>
                  <input
                    type="text"
                    value={settings.setupDetails.keyboardMake || ''}
                    onChange={(e) => setSettings({...settings, setupDetails: {...settings.setupDetails, keyboardMake: e.target.value}})}
                    placeholder="e.g., Keychron, Ducky"
                  />
                </div>
                <div className="form-group">
                  <label>Keyboard Model</label>
                  <input
                    type="text"
                    value={settings.setupDetails.keyboardModel || ''}
                    onChange={(e) => setSettings({...settings, setupDetails: {...settings.setupDetails, keyboardModel: e.target.value}})}
                    placeholder="e.g., K2, One 2 Mini"
                  />
                </div>
                <div className="form-group">
                  <label>Switches</label>
                  <input
                    type="text"
                    value={settings.setupDetails.switches || ''}
                    onChange={(e) => setSettings({...settings, setupDetails: {...settings.setupDetails, switches: e.target.value}})}
                    placeholder="e.g., Cherry MX Red, Gateron Brown"
                  />
                </div>
                <div className="form-group">
                  <label>Operating System</label>
                  <select
                    value={settings.setupDetails.os || ''}
                    onChange={(e) => setSettings({...settings, setupDetails: {...settings.setupDetails, os: e.target.value}})}
                  >
                    <option value="">Select OS</option>
                    <option value="Windows">Windows</option>
                    <option value="macOS">macOS</option>
                    <option value="Linux">Linux</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Display Refresh Rate (Hz)</label>
                  <input
                    type="number"
                    value={settings.setupDetails.refreshRate || 60}
                    onChange={(e) => setSettings({...settings, setupDetails: {...settings.setupDetails, refreshRate: parseInt(e.target.value)}})}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Social Links - Collapsible */}
          <div className="form-section collapsible">
            <button 
              className="section-toggle"
              onClick={() => setSocialExpanded(!socialExpanded)}
            >
              <span>Social Links</span>
              <span className="toggle-icon">{socialExpanded ? '▼' : '▶'}</span>
            </button>
            {socialExpanded && (
              <div className="section-content">
                <div className="form-group">
                  <label>Twitter/X Handle</label>
                  <input
                    type="text"
                    value={settings.socialLinks.twitter || ''}
                    onChange={(e) => setSettings({...settings, socialLinks: {...settings.socialLinks, twitter: e.target.value}})}
                    placeholder="@username or URL"
                  />
                  {settings.socialLinks.twitter && !validateTwitterHandle(settings.socialLinks.twitter) && (
                    <span className="validation-error">Invalid Twitter handle</span>
                  )}
                </div>
                <div className="form-group">
                  <label>GitHub Username</label>
                  <input
                    type="text"
                    value={settings.socialLinks.github || ''}
                    onChange={(e) => setSettings({...settings, socialLinks: {...settings.socialLinks, github: e.target.value}})}
                    placeholder="username or URL"
                  />
                  {settings.socialLinks.github && !validateGitHubUsername(settings.socialLinks.github) && (
                    <span className="validation-error">Invalid GitHub username</span>
                  )}
                </div>
                <div className="form-group">
                  <label>YouTube Channel</label>
                  <input
                    type="text"
                    value={settings.socialLinks.youtube || ''}
                    onChange={(e) => setSettings({...settings, socialLinks: {...settings.socialLinks, youtube: e.target.value}})}
                    placeholder="@channel or URL"
                  />
                  {settings.socialLinks.youtube && !validateYouTubeUrl(settings.socialLinks.youtube) && (
                    <span className="validation-error">Invalid YouTube URL</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="user-menu-footer">
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges || (usernameStatus !== 'unchanged' && usernameStatus !== 'available')}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
